import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import clsx from 'clsx';
import { BookOpenText, Search } from '@/components/icons';
import { useAuth } from '@/lib/auth';
import { Card, Input, PageHeader } from '@/components/ui';
import { GUIDE_ROLE_LABEL, GUIDE_SECTIONS, QUICK_START, searchGuide, type GuideRole } from '@/features/guide/sections';

/** The role whose quick start opens first: the signed-in person's own. */
function roleOf(platformRole: string | undefined, orgRole: string | null): GuideRole {
  if (platformRole === 'ADMIN') return 'ADMIN';
  if (orgRole === 'PROJECT_ADMIN' || orgRole === 'MEMBER' || orgRole === 'VIEWER') return orgRole;
  return platformRole === 'PROJECT_ADMIN' ? 'PROJECT_ADMIN' : 'VIEWER';
}

const byId = new Map(GUIDE_SECTIONS.map((s) => [s.id, s]));
const GROUPS = [...new Set(GUIDE_SECTIONS.map((s) => s.group))];

/**
 * How to use 1QA: the whole product, end to end, for every role. Sections have stable anchors
 * (/guide#recorder) so other pages can link to them.
 */
export function GuidePage() {
  const { user, currentOrgRole } = useAuth();
  const mine = roleOf(user?.platformRole, currentOrgRole);
  const [role, setRole] = useState<GuideRole>(mine);
  const [query, setQuery] = useState('');
  const { hash } = useLocation();
  const shown = useMemo(() => searchGuide(GUIDE_SECTIONS, query), [query]);

  // Deep links: scroll to /guide#<section> once it is rendered.
  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    if (id && byId.has(id)) document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [hash]);

  const quick = QUICK_START[role];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Guide"
        title={
          <span className="flex items-center gap-3">
            <BookOpenText className="size-6 text-primary" aria-hidden /> How to use 1QA
          </span>
        }
        description="Everything from the first project to automated runs in your CI, in the order you will need it."
      />

      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-xs font-semibold tracking-label text-fg-muted uppercase">Quick start for</span>
          {(Object.keys(QUICK_START) as GuideRole[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRole(r)}
              aria-pressed={role === r}
              className={clsx(
                'rounded-full px-3 py-1 text-xs font-medium ring-1 transition ring-inset',
                role === r ? 'bg-primary text-primary-fg ring-primary' : 'text-fg-secondary ring-border hover:text-fg',
              )}
            >
              {GUIDE_ROLE_LABEL[r]}
              {r === mine && ' (you)'}
            </button>
          ))}
        </div>
        <p className="mt-3 text-sm text-fg-secondary">{quick.intro}</p>
        <ol className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="guide-quick-start">
          {quick.sections.map((id, i) => {
            const s = byId.get(id)!;
            return (
              <li key={id}>
                <a href={`#${id}`} className="flex h-full gap-3 rounded-card bg-surface-sunken p-3 ring-1 ring-border transition ring-inset hover:ring-primary">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary-subtle text-xs font-semibold text-primary">{i + 1}</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-fg">{s.title}</span>
                    <span className="mt-0.5 block text-xs text-fg-muted">{s.summary}</span>
                  </span>
                </a>
              </li>
            );
          })}
        </ol>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-fg-muted" aria-hidden />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the guide"
              aria-label="Search the guide"
              className="pl-8"
            />
          </div>
          <nav aria-label="Guide contents" className="mt-4 hidden space-y-4 lg:block">
            {GROUPS.map((g) => {
              const items = shown.filter((s) => s.group === g);
              if (!items.length) return null;
              return (
                <div key={g}>
                  <div className="mb-1 text-[11px] font-semibold tracking-label text-fg-muted uppercase">{g}</div>
                  <ul className="space-y-0.5">
                    {items.map((s) => (
                      <li key={s.id}>
                        <a
                          href={`#${s.id}`}
                          className={clsx(
                            'block rounded-bar px-2 py-1 text-sm transition hover:bg-surface hover:text-fg',
                            hash === `#${s.id}` ? 'bg-surface font-medium text-primary' : 'text-fg-secondary',
                          )}
                        >
                          {s.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </nav>
        </aside>

        <div className="min-w-0 space-y-6">
          {shown.length === 0 && (
            <Card className="p-6 text-sm text-fg-secondary" role="status">
              Nothing in the guide matches “{query}”. Try another word, such as <em>run</em>, <em>share</em> or <em>password</em>.
            </Card>
          )}
          {shown.map((s) => (
            <Card key={s.id} id={s.id} className="scroll-mt-24 p-6" data-testid="guide-section">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold tracking-label text-fg-muted uppercase">{s.group}</div>
                  <h2 className="mt-1 text-lg font-semibold text-fg">
                    <a href={`#${s.id}`} className="hover:text-primary">
                      {s.title}
                    </a>
                  </h2>
                  <p className="mt-1 text-sm text-fg-muted">{s.summary}</p>
                </div>
                {s.roles.length < 4 && (
                  <div className="flex flex-wrap gap-1.5" aria-label="Mainly for">
                    {s.roles.map((r) => (
                      <span
                        key={r}
                        className={clsx(
                          'rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
                          r === mine ? 'bg-primary-subtle text-primary ring-primary/30' : 'text-fg-muted ring-border',
                        )}
                      >
                        {GUIDE_ROLE_LABEL[r]}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-4 space-y-3 text-sm">{s.body}</div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
